import { useFormContext } from "react-hook-form";
import { STRUCTURE_TYPE, STRUCTURE_TYPE_LABELS } from "@user-management-context/read/domain/types/admin/teacher";
import type { PublicStudioListVM } from "@shared-kernel/application/ports/public/public-repository";
import { RecapRow, RecapSection } from "@shared-kernel/primary/shared/onboarding/recap-section";
import { PlainTextContent } from "@shared-kernel/primary/shared/plain-text-content/plain-text-content.content";
import { CollapsiblePlainTextContent } from "@shared-kernel/primary/shared/plain-text-content/collapsible-plain-text-content.content";
import { getLeafLabel } from "@academy-context/shared/domain/types/enums/specializations";
import { ONBOARDING_STEP, ONBOARDING_STEP_LABELS, STUDIO_OPTION } from "../form-validation/shared";
import type { TeacherOnboardingFormInputs } from "../form-validation/full-form.schema";
import { formatActivityRegistrationNumber, formatIban, formatSiret } from "@utils/formatting";

const TRUNCATE_LIMIT = 200;
const truncate = (value: string): string => (value.length > TRUNCATE_LIMIT ? `${value.slice(0, TRUNCATE_LIMIT)}…` : value);

interface RecapStepProps {
  publicStudios: PublicStudioListVM[];
  onEditStep: (stepId: number) => void;
}

export const RecapStep = ({ publicStudios, onEditStep }: RecapStepProps) => {
  const { getValues } = useFormContext<TeacherOnboardingFormInputs>();
  const v = getValues();
  const showBusinessName = v.structureType === STRUCTURE_TYPE.ASSOCIATION || v.structureType === STRUCTURE_TYPE.ENTREPRISE;
  const selectedStudio = v.studio.studioId ? publicStudios.find(s => s.id === v.studio.studioId) : undefined;
  const details = v.studio.details;

  return (
    <div className="space-y-4">
      <RecapSection title={ONBOARDING_STEP_LABELS[ONBOARDING_STEP.ACTIVITY]} onEdit={() => onEditStep(ONBOARDING_STEP.ACTIVITY)}>
        <RecapRow label="Type de structure" value={STRUCTURE_TYPE_LABELS[v.structureType]} />
        {showBusinessName && <RecapRow label="Raison sociale" value={v.businessName} />}
        <RecapRow label="SIRET" value={formatSiret(v.siret)} />
        <RecapRow label="N° de déclaration d'activité" value={formatActivityRegistrationNumber(v.activityRegistrationNumber)} />
        <RecapRow label="Adresse postale" value={v.postalAddress.description} />
        {v.addressComplement && <RecapRow label="Complément" value={v.addressComplement} />}
      </RecapSection>
      <RecapSection title={ONBOARDING_STEP_LABELS[ONBOARDING_STEP.PEDAGOGY]} onEdit={() => onEditStep(ONBOARDING_STEP.PEDAGOGY)}>
        <RecapRow label="CV pédagogique" value={v.teacherResumeUrl ? "Téléchargé" : "—"} />
        <RecapRow label="CV artistique" value={v.artistResumeUrl ? "Téléchargé" : "—"} />
        <RecapRow label="Certifications" value={v.certifications.length === 0 ? "—" : v.certifications.join(", ")} />
        <RecapRow
          label="Méthodes pédagogiques"
          value={v.teachingMethodologies.length === 0 ? "—" : v.teachingMethodologies.map(m => m.title).join(", ")}
        />
      </RecapSection>
      <RecapSection
        title={ONBOARDING_STEP_LABELS[ONBOARDING_STEP.PUBLIC_PROFILE]}
        onEdit={() => onEditStep(ONBOARDING_STEP.PUBLIC_PROFILE)}
      >
        <RecapRow label="Profil public visible" value={v.isPublicProfileVisibleByTeacher ? "Oui" : "Non"} />
        <RecapRow label="Email visible" value={v.isEmailVisibleOnPublicProfile ? "Oui" : "Non"} />
        <RecapRow label="Description" value={v.description ? truncate(v.description) : "—"} />
        <RecapRow
          label="Expérience pédagogique"
          value={v.pedagogicalExperience ? <CollapsiblePlainTextContent content={v.pedagogicalExperience} /> : "—"}
        />
        <RecapRow
          label="Spécialités"
          value={v.specializations.length === 0 ? "—" : v.specializations.map(id => getLeafLabel(id)).join(", ")}
        />
        <RecapRow label="Coachings en visio" value={v.offersOnlineCoaching ? "Oui" : "Non"} />
        {v.websiteUrl && <RecapRow label="Site web" value={v.websiteUrl} />}
        {v.instagramUrl && <RecapRow label="Instagram" value={v.instagramUrl} />}
        {v.youtubeUrl && <RecapRow label="YouTube" value={v.youtubeUrl} />}
        {v.tiktokUrl && <RecapRow label="TikTok" value={v.tiktokUrl} />}
        {v.facebookUrl && <RecapRow label="Facebook" value={v.facebookUrl} />}
      </RecapSection>
      <RecapSection title={ONBOARDING_STEP_LABELS[ONBOARDING_STEP.BILLING]} onEdit={() => onEditStep(ONBOARDING_STEP.BILLING)}>
        <RecapRow label="Tarif horaire standard" value={v.defaultPrice !== null ? `${v.defaultPrice} €/heure` : "—"} />
        <RecapRow label="IBAN" value={formatIban(v.iban)} />
      </RecapSection>
      <RecapSection title={ONBOARDING_STEP_LABELS[ONBOARDING_STEP.STUDIO]} onEdit={() => onEditStep(ONBOARDING_STEP.STUDIO)}>
        {v.studio.option === STUDIO_OPTION.PARTNER && <RecapRow label="Studio partenaire" value={selectedStudio?.name ?? "—"} />}
        {(v.studio.option === STUDIO_OPTION.PERSONAL || v.studio.option === STUDIO_OPTION.OTHER) && details && (
          <>
            <RecapRow label="Nom du studio" value={details.name} />
            <RecapRow label="Adresse" value={details.postalAddress.description} />
            {details.addressComplement && <RecapRow label="Complément" value={details.addressComplement} />}
            {details.equipmentDescription && (
              <RecapRow label="Équipement" value={<PlainTextContent content={details.equipmentDescription} />} />
            )}
            <RecapRow label="Tarif horaire" value={details.price !== null ? `${details.price} €/heure` : "—"} />
            <RecapRow label="Accessible PMR" value={details.isMobilityAccessible ? "Oui" : "Non"} />
          </>
        )}
      </RecapSection>
    </div>
  );
};
